import React, { useEffect, useState, useCallback } from 'react';
import {
  Tabs, Card, Button, message, Table, Tag, Popconfirm, Select, Spin,
  Modal, Form, Input, Radio, Checkbox, Switch, Space, List, Empty,
} from 'antd';
import { PlusOutlined, ClockCircleOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  getKvkkTypes,
  createKvkkType,
  updateKvkkType,
  deleteKvkkType,
  getKvkkText,
  updateKvkkText,
  getKvkkConsents,
  revokeKvkkConsent,
  getKvkkPendingUsers,
} from '../../api/kvkk';
import { getBranches } from '../../api/branches';
import UserSearchSelect from '../common/UserSearchSelect';
import usePersistedPageSize from '../../hooks/usePersistedPageSize';
import useServerSort from '../../hooks/useServerSort';
import useAuthStore from '../../store/authStore';
import dayjs from 'dayjs';
import { apiErrorMessage } from '../../utils/apiError';

const { TabPane } = Tabs;

const ROLE_OPTIONS = [
  { value: 'developer_admin', label: 'Geliştirici Admin' },
  { value: 'super_admin', label: 'Süper Admin' },
  { value: 'branch_manager', label: 'Şube Müdürü' },
  { value: 'teacher', label: 'Öğretmen' },
  { value: 'stajyer', label: 'Stajyer' },
  { value: 'parent', label: 'Veli' },
];

// Hedefleme özeti: "Ayarlar" tablosunda ve düzenleme modalında ortak kullanılıyor.
const targetSummary = (type) => {
  if (type.user_scope_users?.length > 0) {
    return `Sadece: ${type.user_scope_users.map(u => u.name).join(', ')}`;
  }
  const parts = [];
  if (type.scope === 'user') {
    parts.push((type.role_scope || []).map(r => ROLE_OPTIONS.find(o => o.value === r)?.label || r).join(', '));
  }
  parts.push(type.branch_scope_names?.length > 0 ? type.branch_scope_names.join(', ') : 'Tüm şubeler');
  return parts.filter(Boolean).join(' — ');
};

// Ekleme ve düzenleme için tek form - create modunda type=null.
const TypeFormModal = ({ open, type, onClose, onSaved, branches }) => {
  const { user: currentUser } = useAuthStore();
  const isBranchManager = currentUser?.role === 'branch_manager';
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const scope = Form.useWatch('scope', form);
  const isEdit = !!type;
  const hasHistory = !!type?.latest_version || !!type?.has_consents;

  useEffect(() => {
    if (!open) return;
    if (type) {
      form.setFieldsValue({
        label: type.label,
        scope: type.scope,
        role_scope: type.role_scope || [],
        branch_scope: isBranchManager ? (currentUser?.branch_id ? [currentUser.branch_id] : []) : (type.branch_scope || []),
        user_scope: type.user_scope || [],
        is_mandatory: type.is_mandatory,
        is_active: type.is_active,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        scope: 'user',
        is_mandatory: false,
        branch_scope: isBranchManager && currentUser?.branch_id ? [currentUser.branch_id] : [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (isEdit) {
        // Sube muduru icin branch_scope alani disabled ama form yine de
        // degeri (her zaman [kendi subesi]) submit ediyor - bu, super_admin'in
        // olusturdugu "tum subeler" hedefli bir metni sube muduru sirf baska
        // bir alani degistirmek icin actiginda sessizce daraltirdi. Alani
        // payload'dan cikarip mevcut degerin degismeden kalmasini sagliyoruz.
        if (isBranchManager) {
          delete values.branch_scope;
        }
        await updateKvkkType(type.id, values);
        message.success('Metin türü güncellendi.');
      } else {
        await createKvkkType(values);
        message.success('Metin türü oluşturuldu. Şimdi "Metin" sekmesinden içeriğini girebilirsiniz.');
      }
      onSaved();
      onClose();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(apiErrorMessage(error, 'Kaydedilemedi.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isEdit ? 'Metin Türünü Düzenle' : 'Yeni Onay Metni Türü'}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={saving}
      okText={isEdit ? 'Kaydet' : 'Oluştur'}
      cancelText="Vazgeç"
      destroyOnClose
      width={640}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="label" label="Başlık" rules={[{ required: true, message: 'Başlık zorunludur.' }]}>
          <Input placeholder="ör. Kamera Kaydı Onayı" />
        </Form.Item>
        <Form.Item name="scope" label="Kapsam" rules={[{ required: true }]}>
          <Radio.Group disabled={hasHistory}>
            <Radio value="user">Kullanıcı (personel/veli kendisi için onaylar)</Radio>
            <Radio value="student">Öğrenci (veli, çocuğu adına onaylar)</Radio>
          </Radio.Group>
        </Form.Item>
        {hasHistory && (
          <p className="text-xs text-gray-400 -mt-3 mb-3">
            Bu türe ait metin/onay kayıtları olduğu için kapsam değiştirilemez.
          </p>
        )}

        {scope === 'user' && (
          <Form.Item
            name="role_scope"
            label="Hangi roller onaylamalı?"
            rules={[{ required: true, message: 'En az bir rol seçin.' }]}
          >
            <Checkbox.Group options={ROLE_OPTIONS} />
          </Form.Item>
        )}

        <Form.Item
          name="branch_scope"
          label="Hangi şubeler? (boş = tüm şubeler)"
          extra={isBranchManager ? 'Şube müdürü olarak sadece kendi şubenizi hedefleyebilirsiniz.' : undefined}
        >
          <Select
            mode="multiple"
            allowClear
            disabled={isBranchManager}
            placeholder="Tüm şubeler"
            options={branches.map(b => ({ value: b.id, label: b.name }))}
          />
        </Form.Item>

        {scope === 'user' && (
          <Form.Item
            name="user_scope"
            label="Belirli kullanıcılara daralt (opsiyonel — doldurulursa yukarıdaki rol/şube seçimini geçersiz kılar, sadece seçilenler görür)"
          >
            <UserSearchSelect mode="multiple" placeholder="Aramak için en az 2 karakter yazın (boş = daraltma yok)" />
          </Form.Item>
        )}

        <div className="flex items-center gap-6">
          <Form.Item name="is_mandatory" label="Zorunlu mu?" valuePropName="checked" className="mb-0">
            <Switch checkedChildren="Zorunlu" unCheckedChildren="Opsiyonel" />
          </Form.Item>
          {isEdit && (
            <Form.Item name="is_active" label="Aktif mi?" valuePropName="checked" className="mb-0">
              <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
            </Form.Item>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Zorunlu ise onaylanmadan (öğrenci bazlıysa velinin ilgili öğrencisi için onaylamasından önce) sisteme giriş
          yapılamaz. Opsiyonel ise reddedilebilir, girişi engellemez.
        </p>
      </Form>
    </Modal>
  );
};

const TypesSettingsTable = ({ types, branches, onTypesChange }) => {
  const [formOpen, setFormOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);

  const openCreate = () => { setEditingType(null); setFormOpen(true); };
  const openEdit = (type) => { setEditingType(type); setFormOpen(true); };

  const handleDelete = async (type) => {
    try {
      await deleteKvkkType(type.id);
      message.success('Metin türü silindi.');
      onTypesChange();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Silinemedi.'));
    }
  };

  const columns = [
    { title: 'Başlık', dataIndex: 'label' },
    {
      title: 'Kapsam',
      dataIndex: 'scope',
      render: (scope) => scope === 'user' ? <Tag>Kullanıcı</Tag> : <Tag color="blue">Öğrenci</Tag>,
    },
    {
      title: 'Hedef Kitle',
      render: (_, type) => <span className="text-sm text-gray-600">{targetSummary(type)}</span>,
    },
    {
      title: 'Zorunlu',
      dataIndex: 'is_mandatory',
      render: (v) => v ? <Tag color="red">Zorunlu</Tag> : <Tag color="default">Opsiyonel</Tag>,
    },
    {
      title: 'Durum',
      dataIndex: 'is_active',
      render: (v) => v ? <Tag color="green">Aktif</Tag> : <Tag color="default">Pasif</Tag>,
    },
    {
      title: 'İşlemler',
      render: (_, type) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(type)}>Düzenle</Button>
          <Popconfirm
            title="Bu metin türünü silmek istediğinize emin misiniz?"
            description="Metin/onay kaydı varsa silme engellenir, bunun yerine pasife alın."
            onConfirm={() => handleDelete(type)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button size="small" danger icon={<DeleteOutlined />}>Sil</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Onay Metni Türleri"
      extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Yeni Metin Türü</Button>}
    >
      <Table rowKey="id" columns={columns} dataSource={types} pagination={false} />
      <TypeFormModal
        open={formOpen}
        type={editingType}
        branches={branches}
        onClose={() => setFormOpen(false)}
        onSaved={onTypesChange}
      />
    </Card>
  );
};

const TextEditorTab = ({ types }) => {
  const selectableTypes = types.filter(t => !!t.key);
  const [selectedKey, setSelectedKey] = useState(selectableTypes[0]?.key);
  const [content, setContent] = useState('');
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!selectableTypes.find(t => t.key === selectedKey)) {
      setSelectedKey(selectableTypes[0]?.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [types, selectedKey]);

  const fetchText = useCallback(async () => {
    if (!selectedKey) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await getKvkkText(selectedKey);
      if (res.data.data) {
        setContent(res.data.data.content);
        setVersion(res.data.data.version);
      } else {
        setContent('');
        setVersion(null);
      }
    } catch {
      message.error('Metin yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [selectedKey]);

  useEffect(() => { fetchText(); }, [fetchText]);

  const handleSave = async () => {
    if (!content.trim()) {
      message.warning('Metin boş olamaz.');
      return;
    }
    setSaving(true);
    try {
      await updateKvkkText(selectedKey, { content });
      message.success('Metin güncellendi. Yeni versiyon oluşturuldu.');
      fetchText();
    } catch {
      message.error('Metin kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const brokenCount = types.length - selectableTypes.length;

  return (
    <div>
      {brokenCount > 0 && (
        <div className="mb-4 text-sm text-red-500">
          {brokenCount} metin türünün başlığından geçerli bir anahtar üretilemediği için listede gösterilmiyor.
        </div>
      )}
      <Select
        value={selectedKey}
        onChange={setSelectedKey}
        style={{ width: 360, marginBottom: 16 }}
        options={selectableTypes.map(t => ({
          value: t.key,
          label: `${t.label}${t.is_active ? '' : ' (pasif)'}`,
        }))}
        placeholder="Bir metin türü seçin"
      />
      {loading ? <Spin /> : selectedKey && (
        <Card
          title={`İçerik ${version ? `(Versiyon ${version})` : '(henüz metin girilmedi)'}`}
          extra={
            <Button type="primary" onClick={handleSave} loading={saving}>
              Kaydet (Yeni Versiyon)
            </Button>
          }
        >
          <p className="text-sm text-gray-500 mb-3">
            Kaydettiğinizde yeni bir versiyon oluşturulur ve tüm ilgililerden tekrar onay alınır.
          </p>
          <ReactQuill value={content} onChange={setContent} theme="snow" />
        </Card>
      )}
    </div>
  );
};

const STATUS_TAGS = {
  active: <Tag color="green">Aktif</Tag>,
  revoked: <Tag color="default">İptal</Tag>,
  rejected: <Tag color="red">Reddedildi</Tag>,
};

const PendingModal = ({ type, typeLabel, onClose }) => {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getKvkkPendingUsers(type)
      .then(res => setItems(res.data.data || []))
      .catch(() => message.error('Bekleyenler listesi yüklenemedi.'))
      .finally(() => setLoading(false));
  }, [type]);

  return (
    <Modal open onCancel={onClose} footer={null} title={`Bekleyenler — ${typeLabel}`}>
      {loading ? (
        <div className="flex justify-center py-8"><Spin /></div>
      ) : items.length === 0 ? (
        <Empty description="Herkes karar vermiş." />
      ) : (
        <List
          size="small"
          dataSource={items}
          renderItem={(item) => (
            <List.Item>
              {item.name}{item.role ? <span className="text-gray-400 ml-2 text-xs">{item.role}</span> : null}
            </List.Item>
          )}
        />
      )}
    </Modal>
  );
};

const ConsentsTable = ({ types }) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [persistedPageSize, setPersistedPageSize] = usePersistedPageSize('kvkkConsents', 20);
  const [pagination, setPagination] = useState({ current: 1, pageSize: persistedPageSize, total: 0 });
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pendingModalOpen, setPendingModalOpen] = useState(false);
  const { sortField, sortOrder, applySorter } = useServerSort();

  const fetchConsents = useCallback(async (page = 1, perPage = pagination.pageSize, sortBy = sortField, sortDir = sortOrder ? (sortOrder === 'descend' ? 'desc' : 'asc') : null) => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage, sort_by: sortBy || undefined, sort_direction: sortDir || undefined };
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await getKvkkConsents(params);
      setData(res.data.data);
      setPagination({ current: res.data.current_page, pageSize: res.data.per_page, total: res.data.total });
    } catch {
      message.error('Onaylar yüklenemedi.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, statusFilter, pagination.pageSize, sortField, sortOrder]);

  useEffect(() => {
    fetchConsents(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, statusFilter]);

  const handleRevoke = async (id) => {
    try {
      await revokeKvkkConsent(id);
      message.success('Onay iptal edildi.');
      fetchConsents(pagination.current);
    } catch {
      message.error('Onay iptal edilemedi.');
    }
  };

  const typeLabel = (typeKey) => types.find(t => t.key === typeKey)?.label || typeKey;

  const columns = [
    {
      title: 'Kullanıcı',
      render: (_, r) => r.user ? (
        <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(`/users/${r.user.id}`)}>
          {r.user.name} {r.user.surname}
        </Button>
      ) : '—',
    },
    {
      title: 'Öğrenci',
      render: (_, r) => r.student ? `${r.student.name} ${r.student.surname}` : <span className="text-gray-400">—</span>,
    },
    {
      title: 'Tür',
      render: (_, r) => typeLabel(r.kvkk_text?.type),
    },
    {
      title: 'Versiyon',
      render: (_, r) => `v${r.kvkk_text?.version}`,
    },
    {
      title: 'Tarih',
      key: 'accepted_at',
      sorter: true,
      sortOrder: sortField === 'accepted_at' ? sortOrder : null,
      render: (_, r) => dayjs(r.accepted_at).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Durum',
      key: 'status',
      sorter: true,
      sortOrder: sortField === 'status' ? sortOrder : null,
      render: (_, r) => STATUS_TAGS[r.status] || r.status,
    },
    {
      title: 'İşlem',
      render: (_, r) => r.status === 'active' ? (
        <Popconfirm
          title="Onayı iptal etmek istediğinize emin misiniz?"
          onConfirm={() => handleRevoke(r.id)}
          okText="Evet"
          cancelText="Hayır"
        >
          <Button danger size="small">İptal Et</Button>
        </Popconfirm>
      ) : (
        <span className="text-gray-400 text-sm">
          {r.revoked_at ? dayjs(r.revoked_at).format('DD.MM.YYYY') : '-'}
        </span>
      ),
    },
  ];

  return (
    <Card title="Onaylayanlar">
      <div className="flex gap-3 mb-4">
        <Select
          value={typeFilter || undefined}
          onChange={val => setTypeFilter(val ?? '')}
          allowClear
          placeholder="Tür filtrele"
          style={{ width: 260 }}
          options={types.filter(t => !!t.key).map(t => ({ value: t.key, label: t.label }))}
        />
        <Select
          value={statusFilter || undefined}
          onChange={val => setStatusFilter(val ?? '')}
          allowClear
          placeholder="Durum filtrele"
          style={{ width: 160 }}
          options={[
            { value: 'active', label: 'Aktif' },
            { value: 'rejected', label: 'Reddedildi' },
            { value: 'revoked', label: 'İptal' },
          ]}
        />
        <Button
          icon={<ClockCircleOutlined />}
          disabled={!typeFilter}
          onClick={() => setPendingModalOpen(true)}
        >
          Bekleyenler
        </Button>
      </div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        onChange={(paginationInfo, _filters, sorter) => {
          const { sortField: sField, sortDirection: sDir } = applySorter(sorter);
          setPersistedPageSize(paginationInfo.pageSize);
          fetchConsents(paginationInfo.current, paginationInfo.pageSize, sField, sDir);
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '200'],
        }}
      />
      {pendingModalOpen && typeFilter && (
        <PendingModal
          type={typeFilter}
          typeLabel={typeLabel(typeFilter)}
          onClose={() => setPendingModalOpen(false)}
        />
      )}
    </Card>
  );
};

const KvkkTab = () => {
  const [types, setTypes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTypes = useCallback(async () => {
    try {
      const res = await getKvkkTypes();
      setTypes(res.data.data || []);
    } catch {
      message.error('Metin türleri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTypes();
    getBranches({ per_page: 100 }).then(res => setBranches(res.data.data || [])).catch(() => {});
  }, [fetchTypes]);

  if (loading) return <div className="flex justify-center py-12"><Spin /></div>;

  return (
    <Tabs defaultActiveKey="settings">
      <TabPane tab="Ayarlar" key="settings">
        <TypesSettingsTable types={types} branches={branches} onTypesChange={fetchTypes} />
      </TabPane>
      <TabPane tab="Metin" key="text">
        <TextEditorTab types={types} />
      </TabPane>
      <TabPane tab="Onaylayanlar" key="consents">
        <ConsentsTable types={types} />
      </TabPane>
    </Tabs>
  );
};

export default KvkkTab;
